import { useState } from "react";
import { Columns3 } from "lucide-react";
import { Button } from "@/components/ui/PageHeader";
import { assist, type AssistanceContinuation, type AssistanceIntent, type AssistanceResponse } from "@/api/composer";

/** Diagnostic comparison only. It cannot save, publish or book an option. */
export function AssistanceComparisonPanel({ sources, text, intent, continuation, disabled = false }: {
  sources: AssistanceResponse["retrieval"]["sources"];
  text: string;
  intent: AssistanceIntent;
  continuation?: AssistanceContinuation;
  disabled?: boolean;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [result, setResult] = useState<AssistanceResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const keyOf = (source: typeof sources[number]) => `${source.reference.type}:${source.reference.id}`;
  const unique = [...new Map(sources.map(source => [keyOf(source), source])).values()];
  const byKey = new Map(unique.map(source => [keyOf(source), source]));
  const chosen = selected.flatMap(key => byKey.get(key) ?? []);
  async function compare() {
    setBusy(true); setError(""); setResult(null);
    try {
      setResult((await assist(text, intent, undefined, { ...continuation,
        comparison: { sourceReferences: chosen.map(source => source.reference) },
      })).data);
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Comparison failed"); }
    finally { setBusy(false); }
  }
  const comparison = result?.comparison;
  const fields = [...new Map(comparison?.options.flatMap(source => source.facts ?? [])
    .map(fact => [fact.field, fact.label]) ?? []).entries()];
  return <section className="space-y-3 rounded-lg border p-3" aria-label="Compare selected records">
    <h4 className="font-semibold">Compare selected records</h4>
    <p className="text-xs">Select two to four records. Compare checks access again and reads current record fields.
      It does not run a new search or send these records to the model.</p>
    <fieldset disabled={busy || disabled} className="space-y-2">
      <legend className="sr-only">Records to compare</legend>
      {unique.map(source => <label key={keyOf(source)} className="flex items-start gap-2">
        <input type="checkbox" checked={selected.includes(keyOf(source))}
          disabled={!selected.includes(keyOf(source)) && chosen.length >= 4}
          onChange={event => { setResult(null); setSelected(current => event.target.checked
            ? [...current, keyOf(source)] : current.filter(key => key !== keyOf(source))); }} />
        <span>{source.title} <small>({source.reference.type})</small></span>
      </label>)}
    </fieldset>
    <Button size="sm" variant="secondary" disabled={busy || disabled || chosen.length < 2}
      onClick={compare}><Columns3 className="mr-1 h-4 w-4" />Compare selected</Button>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {comparison && <div className="space-y-2" aria-live="polite">
      <p>Comparison: {comparison.status}. Unavailable selections: {comparison.unavailableReferences.length}.</p>
      {comparison.status === "not_run" && <p>{result.message}</p>}
      {result.resources.map(resource => <p key={resource.name}>{resource.name}: {resource.contact}</p>)}
      {comparison.options.length > 0 && <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs">
          <caption className="sr-only">Recorded facts, in selection order</caption>
          <thead><tr><th className="border p-2" scope="col">Recorded field</th>
            {comparison.options.map(source => <th key={keyOf(source)} scope="col" className="min-w-40 border p-2">{source.title}</th>)}
          </tr></thead>
          <tbody>{fields.map(([field, label]) => <tr key={field}><th scope="row" className="border p-2">{label}</th>
            {comparison.options.map(source => <td key={keyOf(source)} className="border p-2">
              {source.facts?.find(fact => fact.field === field)?.value ?? "Not recorded"}</td>)}
          </tr>)}
            <tr><th scope="row" className="border p-2">Member requirements</th>{comparison.options.map(source =>
              <td key={keyOf(source)} className="border p-2">Unverified</td>)}</tr>
            <tr><th scope="row" className="border p-2">Access checked</th>{comparison.options.map(source =>
              <td key={keyOf(source)} className="border p-2">{source.retrievedAt}</td>)}</tr>
            <tr><th scope="row" className="border p-2">Destination</th>{comparison.options.map(source =>
              <td key={keyOf(source)} className="break-all border p-2">{source.destinationPath}</td>)}</tr>
          </tbody>
        </table>
      </div>}
      {comparison.notes.map(note => <p key={note} className="text-xs">{note}</p>)}
    </div>}
  </section>;
}
